/**
 * Content automation helpers shared by the content worker (configuration +
 * engagement capture) and the campaigns orchestrator (auto-poster, auto-post
 * campaign sweep, engagement-to-DM dispatch). Pure functions only.
 */

/** Max length of a per-post comment keyword ("comment GUIDE below"). */
export const DM_KEYWORD_MAX = 40
/** Max length of the message sent to people who comment the keyword. */
export const DM_TEMPLATE_MAX = 1000

/**
 * Weekly automated-post slots the global Settings → Content poster consumes.
 * The poster runs on a day interval (daily / every other day / weekly) that
 * the orchestrator clamps to the plan's autoPostsPerWeek; this turns the same
 * rule into "slots per week" so auto-post campaigns and the global poster can
 * share one allowance honestly. 0 when the poster is off.
 */
export function posterWeeklySlots(
  config: { readonly posterEnabled?: unknown; readonly posterFrequency?: unknown } | null | undefined,
  autoPostsPerWeek: number,
): number {
  if (!config || config.posterEnabled !== true || autoPostsPerWeek <= 0) return 0
  const freq = typeof config.posterFrequency === 'string' ? config.posterFrequency : 'daily'
  const chosen = freq === 'daily' ? 1 : freq === 'every_other_day' ? 2 : 7
  const planMin = autoPostsPerWeek >= 7 ? 1 : Math.ceil(7 / Math.max(1, autoPostsPerWeek))
  const interval = Math.max(chosen, planMin)
  return Math.min(autoPostsPerWeek, Math.ceil(7 / interval))
}

/**
 * Normalize a user-entered comment keyword. Returns null for "no keyword"
 * (empty input) and a string error for invalid input.
 */
export function normalizeDmKeyword(raw: unknown): { value: string | null } | { error: string } {
  if (raw === null || raw === undefined) return { value: null }
  if (typeof raw !== 'string') return { error: 'keyword must be text' }
  const value = raw.trim().replace(/\s+/g, ' ')
  if (value.length === 0) return { value: null }
  if (value.length > DM_KEYWORD_MAX) return { error: `keyword must be at most ${DM_KEYWORD_MAX} characters` }
  if (!/[\p{L}\p{N}]/u.test(value)) return { error: 'keyword must contain a letter or number' }
  return { value }
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Does a comment contain the keyword? Case-insensitive and whole-word, so
 * "GUIDE" matches "guide please!" and "Guide." but not "guidelines".
 */
export function matchesDmKeyword(comment: string | null | undefined, keyword: string | null | undefined): boolean {
  if (!comment || !keyword) return false
  const kw = keyword.trim()
  if (!kw) return false
  const re = new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(kw)}(?=$|[^\\p{L}\\p{N}])`, 'iu')
  return re.test(comment)
}

/** Fill {first_name} in a keyword DM template ("there" when unknown). */
export function fillDmTemplate(template: string, fullName: string | null | undefined): string {
  const first = (fullName ?? '').trim().split(/\s+/)[0] ?? ''
  const name = first.length > 0 ? first : 'there'
  return template.replace(/\{\s*(first_name|firstName|name)\s*\}/g, name).trim()
}
